import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { axiosInstance } from '@/App';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { 
  Shield, Building2, Users, Receipt, LogOut, Eye, 
  Activity, Calendar, CheckCircle2, XCircle, Search 
} from 'lucide-react';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';

function SuperAdminDashboard() {
  const navigate = useNavigate();
  const [businesses, setBusinesses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    // Verificar si es super admin
    const isSuperAdmin = localStorage.getItem('superadmin');
    if (!isSuperAdmin) {
      navigate('/super-admin/login');
      return;
    }

    loadBusinesses();
  }, []);

  const loadBusinesses = async () => {
    try {
      const response = await axiosInstance.get('/super-admin/businesses');
      setBusinesses(response.data.businesses || []);
    } catch (error) {
      console.error('Error cargando empresas:', error);
      toast.error('Error al cargar las empresas');
      if (error.response?.status === 401 || error.response?.status === 403) {
        handleLogout();
      }
    } finally {
      setLoading(false);
    }
  };

  const handleImpersonate = async (businessId) => {
    try {
      const response = await axiosInstance.post(`/super-admin/impersonate/${businessId}`);
      
      // Guardar token de la empresa
      localStorage.setItem('token', response.data.token);
      localStorage.removeItem('superadmin'); // Quitar flag de superadmin
      
      toast.success(`Accediendo como ${response.data.business.business_name}`);
      
      // Redirigir al dashboard de la empresa
      setTimeout(() => {
        navigate('/dashboard');
        window.location.reload(); // Recargar para actualizar contexto
      }, 1000);
    } catch (error) {
      console.error('Error impersonando:', error);
      toast.error('Error al acceder como empresa');
    }
  };

  const handleToggleActive = async (businessId) => {
    try {
      const response = await axiosInstance.put(`/super-admin/businesses/${businessId}/toggle-active`);
      toast.success(response.data.message);
      loadBusinesses(); // Recargar lista
    } catch (error) {
      console.error('Error cambiando estado:', error);
      toast.error('Error al cambiar estado de la empresa');
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('superadmin');
    navigate('/super-admin/login');
  };

  const filteredBusinesses = businesses.filter(business =>
    business.business_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    business.contact_email.toLowerCase().includes(searchTerm.toLowerCase()) ||
    business.business_nif?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const formatDate = (dateStr) => {
    try {
      return format(new Date(dateStr), 'dd/MM/yyyy HH:mm', { locale: es });
    } catch {
      return dateStr;
    }
  };

  const getStatusBadge = (business) => {
    if (!business.is_active) {
      return <Badge variant="destructive">Desactivada</Badge>;
    }
    if (business.is_trial) {
      return <Badge className="bg-yellow-500">Trial</Badge>;
    }
    if (business.stripe_subscription_status === 'active') {
      return <Badge className="bg-green-500">Activa</Badge>;
    }
    return <Badge variant="outline">{business.stripe_subscription_status}</Badge>;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-100 flex items-center justify-center">
        <div className="text-slate-600">Cargando panel...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-100">
      {/* Header */}
      <div className="bg-slate-900 text-white py-4 px-6">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Shield className="h-8 w-8" />
            <div>
              <h1 className="text-2xl font-bold">OSAGEST Super Admin</h1>
              <p className="text-sm text-slate-300">Panel de gestión de empresas</p>
            </div>
          </div>
          <Button variant="outline" onClick={handleLogout} className="text-white border-white hover:bg-slate-800">
            <LogOut className="h-4 w-4 mr-2" />
            Cerrar Sesión
          </Button>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto p-6">
        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-6">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium">Total Empresas</CardTitle>
              <Building2 className="h-4 w-4 text-slate-600" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{businesses.length}</div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium">Activas</CardTitle>
              <Activity className="h-4 w-4 text-green-600" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-green-600">
                {businesses.filter(b => b.is_active && !b.is_trial).length}
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium">En Trial</CardTitle>
              <Calendar className="h-4 w-4 text-yellow-600" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-yellow-600">
                {businesses.filter(b => b.is_trial).length}
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium">Ingresos MRR</CardTitle>
              <Receipt className="h-4 w-4 text-blue-600" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-blue-600">
                {businesses.reduce((sum, b) => {
                  // Solo contar empresas activas no en trial
                  if (b.is_active && !b.is_trial) {
                    // Buscar precio del plan (aproximado basado en nombre)
                    if (b.plan_name === 'Básico') return sum + 49;
                    if (b.plan_name === 'Profesional') return sum + 99;
                    if (b.plan_name === 'Empresarial') return sum + 199;
                  }
                  return sum;
                }, 0)}€
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Search */}
        <Card className="mb-6">
          <CardContent className="pt-6">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input
                placeholder="Buscar por nombre, email o NIF..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
          </CardContent>
        </Card>

        {/* Businesses Table */}
        <Card>
          <CardHeader>
            <CardTitle>Empresas Registradas</CardTitle>
            <CardDescription>
              {filteredBusinesses.length} de {businesses.length} empresas
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {filteredBusinesses.map((business) => (
                <Card key={business.id} className="hover:shadow-md transition-shadow">
                  <CardContent className="pt-6">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <h3 className="text-lg font-semibold">{business.business_name}</h3>
                          {getStatusBadge(business)}
                          <Badge variant="outline">{business.plan_name}</Badge>
                        </div>
                        
                        <div className="grid grid-cols-2 gap-4 text-sm text-slate-600 mb-3">
                          <div>
                            <p><strong>NIF:</strong> {business.business_nif || 'N/A'}</p>
                            <p><strong>Email:</strong> {business.business_email}</p>
                            <p><strong>Teléfono:</strong> {business.business_phone}</p>
                          </div>
                          <div>
                            <p><strong>Contacto:</strong> {business.contact_name}</p>
                            <p><strong>Email acceso:</strong> {business.contact_email}</p>
                            <p><strong>Registrado:</strong> {formatDate(business.created_at)}</p>
                          </div>
                        </div>

                        {/* Stats */}
                        {business.stats && (
                          <div className="flex gap-4 text-xs text-slate-500 border-t pt-3">
                            <span><Users className="h-3 w-3 inline mr-1" />{business.stats.users} usuarios</span>
                            <span><Building2 className="h-3 w-3 inline mr-1" />{business.stats.customers} clientes</span>
                            <span><Receipt className="h-3 w-3 inline mr-1" />{business.stats.invoices} facturas</span>
                          </div>
                        )}
                      </div>

                      <div className="flex flex-col gap-2 ml-4">
                        <Button
                          size="sm"
                          onClick={() => handleImpersonate(business.id)}
                          disabled={!business.is_active}
                        >
                          <Eye className="h-3 w-3 mr-1" />
                          Ver como empresa
                        </Button>
                        <Button
                          size="sm"
                          variant={business.is_active ? "outline" : "default"}
                          onClick={() => handleToggleActive(business.id)}
                        >
                          {business.is_active ? (
                            <><XCircle className="h-3 w-3 mr-1" />Desactivar</>
                          ) : (
                            <><CheckCircle2 className="h-3 w-3 mr-1" />Activar</>
                          )}
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}

              {filteredBusinesses.length === 0 && (
                <div className="text-center py-12 text-slate-500">
                  No se encontraron empresas
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

export default SuperAdminDashboard;
